import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import { ITopic } from '../TopicSlice';
import { IBaseUser } from '../UserSlice';
import { IResourceFile } from '../ResourcesSlice';
import { getGroupedSubTopics, getSubTopicById } from './SubTopicApi';
import {
  adminGetSubTopicList,
  adminCreateSubTopic,
  adminDeleteSubTopic,
  adminUpdateSubTopic,
  adminGetSubTopicById,
  adminLoadMoreSubTopicList,
  adminAddSubTopicResources,
  adminRemoveSubTopicResources,
  adminAddSubTopicVideoLink,
  adminRemoveSubTopicVideoLink,
  adminSortSubTopicVideoLink,
  adminEditSubTopicVideoLink,
  adminAddSubTopicContent,
  adminEditSubTopicContent,
  adminRemoveSubTopicContent,
  adminEditSubTopicFAQ,
  adminRemoveSubTopicFAQ,
  adminAddSubTopicFAQ,
  adminAddSubTopicReview,
  adminEditSubTopicReview,
  adminRemoveSubTopicReview,
} from './SubTopicAdminApi';

export interface ISubTopicVideoLink {
  _id: string;
  title: string;
  link: string;
  sortingOrder: number;
  createdAt: number;
  createdBy: string;
}

export interface ISubTopicContent {
  _id: string;
  details: string;
  sortingOrder: number;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
}

export interface ISubTopicFAQ {
  _id: string;
  question: string;
  answer: string;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
}

export interface ISubTopicReview {
  _id: string;
  review: string;
  rating: number;
  reviewer: string;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
}

interface IDiagnostic {
  recommendScore?: number;
  recommendTime?: number;
}

export interface ISubTopic {
  _id: string;
  name: string;
  topic: ITopic;
  sortingOrder?: number;
  createdBy: IBaseUser;
  updatedBy: IBaseUser;
  createdAt: string;
  updatedAt: string;
  resources?: Array<IResourceFile>;
  videoLinks?: Array<ISubTopicVideoLink>;
  contents?: Array<ISubTopicContent>;
  FAQs?: Array<ISubTopicFAQ>;
  reviews?: Array<ISubTopicReview>;
  diagnostic?: IDiagnostic;
  quizCount?: number;
  questionsCount?: number;
}

export interface IGroupedSubTopic {
  _id: string;
  name: string;
  sortingOrder?: string;
  resources?: Array<IResourceFile>;
  videoLinks?: Array<ISubTopicVideoLink>;
  quizCount?: number;
  questionsCount?: number;
  topic?: string;
}

export interface IGroupedTopic {
  _id: string;
  name: string;
  papers?: Array<{ name: string }>;
  subject: string;
  subjectLevel: string;
  subjectCode: string;
  curriculum: string;
  grade: string;
  sortingOrder?: number;
  subTopics: IGroupedSubTopic[];
  isAssessment?: boolean;
}

export interface IGroupedTopicsWithSubTopicsData {
  subject: string;
  curriculum: string;
  grade: string;
  subjectCode: string;
  subjectLevel: string;
  list: IGroupedTopic[];
}

export interface ISubTopicState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  list: Array<ISubTopic>;
  subTopic: ISubTopic | undefined;
  groupedTopicsWithSubTopicsData?: IGroupedTopicsWithSubTopicsData;
}

const initialState: ISubTopicState = {
  status: 'idle',
  count: 0,
  list: [],
  subTopic: undefined,
  groupedTopicsWithSubTopicsData: undefined,
};

export const SubTopicSlice = createSlice({
  name: 'subtopic',
  initialState,
  reducers: {
    resetSubTopicList: (state) => {
      state.list = [];
      state.count = 0;
      state.status = 'idle';
    },
    resetSubTopic: (state) => {
      state.subTopic = initialState.subTopic;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(adminGetSubTopicList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetSubTopicList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(adminGetSubTopicList.rejected, (state) => {
        state.status = 'rejected';
      })
      // CREATE
      .addCase(adminCreateSubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateSubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count += 1;
        const newList = [action.payload, ...state.list];
        state.list = newList;
      })
      .addCase(adminCreateSubTopic.rejected, (state) => {
        state.status = 'rejected';
      })
      // DELETE
      .addCase(adminDeleteSubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminDeleteSubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count -= 1;
        const newList = state.list.filter(
          (item) => item._id !== action.payload._id
        );
        state.list = newList;
      })
      .addCase(adminDeleteSubTopic.rejected, (state) => {
        state.status = 'rejected';
      })
      // UPDATE
      .addCase(adminUpdateSubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminUpdateSubTopic.fulfilled, (state, action) => {
        state.status = 'idle';

        const index = state.list.findIndex(
          (item) => item._id === action.payload._id
        );

        const newList = state.list;

        if (index > -1) {
          newList[index] = action.payload;
        }

        state.list = newList;
      })
      .addCase(adminUpdateSubTopic.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET ONE BY ID ADMIN
      .addCase(adminGetSubTopicById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetSubTopicById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminGetSubTopicById.rejected, (state) => {
        state.status = 'rejected';
        state.subTopic = undefined;
      })
      // GET ONE BY ID
      .addCase(getSubTopicById.pending, (state) => {
        state.subTopic = undefined;
        state.status = 'loading';
      })
      .addCase(getSubTopicById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(getSubTopicById.rejected, (state) => {
        state.status = 'rejected';
      })
      // LOAD MORE
      .addCase(adminLoadMoreSubTopicList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreSubTopicList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreSubTopicList.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get grouped subTopics
      .addCase(getGroupedSubTopics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getGroupedSubTopics.fulfilled, (state, action) => {
        state.status = 'idle';
        state.groupedTopicsWithSubTopicsData = action.payload;
      })
      .addCase(getGroupedSubTopics.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin add new resource
      .addCase(adminAddSubTopicResources.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminAddSubTopicResources.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminAddSubTopicResources.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin remove resources
      .addCase(adminRemoveSubTopicResources.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminRemoveSubTopicResources.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminRemoveSubTopicResources.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin add video link
      .addCase(adminAddSubTopicVideoLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminAddSubTopicVideoLink.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminAddSubTopicVideoLink.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin remove video link
      .addCase(adminRemoveSubTopicVideoLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminRemoveSubTopicVideoLink.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminRemoveSubTopicVideoLink.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin sort subtopic video link
      .addCase(adminSortSubTopicVideoLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminSortSubTopicVideoLink.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminSortSubTopicVideoLink.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin edit subtopic video link
      .addCase(adminEditSubTopicVideoLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminEditSubTopicVideoLink.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminEditSubTopicVideoLink.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin add subtopic content
      .addCase(adminAddSubTopicContent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminAddSubTopicContent.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminAddSubTopicContent.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin edit subtopic content
      .addCase(adminEditSubTopicContent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminEditSubTopicContent.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminEditSubTopicContent.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin remove subtopic content
      .addCase(adminRemoveSubTopicContent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminRemoveSubTopicContent.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminRemoveSubTopicContent.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin add subtopic FAQ
      .addCase(adminAddSubTopicFAQ.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminAddSubTopicFAQ.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminAddSubTopicFAQ.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin edit subtopic FAQ
      .addCase(adminEditSubTopicFAQ.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminEditSubTopicFAQ.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminEditSubTopicFAQ.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin remove subtopic FAQ
      .addCase(adminRemoveSubTopicFAQ.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminRemoveSubTopicFAQ.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminRemoveSubTopicFAQ.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin add subtopic Review
      .addCase(adminAddSubTopicReview.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminAddSubTopicReview.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminAddSubTopicReview.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin edit subtopic Review
      .addCase(adminEditSubTopicReview.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminEditSubTopicReview.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminEditSubTopicReview.rejected, (state) => {
        state.status = 'rejected';
      })
      // Admin remove subtopic Review
      .addCase(adminRemoveSubTopicReview.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminRemoveSubTopicReview.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subTopic = action.payload;
      })
      .addCase(adminRemoveSubTopicReview.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const { resetSubTopicList, resetSubTopic } = SubTopicSlice.actions;

export const getSubTopicState = (state: RootState): RootState['subTopic'] =>
  state.subTopic;

export * from './SubTopicApi';
export * from './SubTopicAdminApi';
export default SubTopicSlice.reducer;
